import Icon from '@mui/material/Icon'
import LinearProgress from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import React from 'react'

import type { SchemaQuota } from '@/__generated/es-api'
import TablePagination from '@/app/component/atom/table-pagination'
import IconText from '@/app/component/layout/icon-text'
import TableRowActions from '@/app/component/layout/table/actions'

type Props = {
  isDeleting: boolean
  quotas: SchemaQuota[]
  onDeleteQuota: (quota: SchemaQuota) => void
}

const pageSizeValues = [
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
]

const getProgress = (quota: SchemaQuota) => {
  if (quota.goal <= 0) {
    return 0
  }

  return Math.min(100, Math.max(0, (quota.used / quota.goal) * 100))
}

const getStatus = (quota: SchemaQuota) => {
  if (quota.campaigns === 0) {
    return { color: 'disabled' as const, icon: 'pause_circle', label: 'Unused', order: 2 }
  }
  if (quota.hit) {
    return { color: 'success' as const, icon: 'check_circle', label: 'Reached', order: 0 }
  }
  return { color: 'primary' as const, icon: 'schedule', label: 'Active', order: 1 }
}

const QuotasTable: React.FC<Props> = ({ isDeleting, quotas, onDeleteQuota }) => {
  const [page, setPage] = React.useState(1)
  const [size, setSize] = React.useState(15)
  const sortedQuotas = React.useMemo(
    () => [...quotas].sort((a, b) => getStatus(a).order - getStatus(b).order),
    [quotas],
  )
  const currentPage = Math.min(page, Math.max(1, Math.ceil(quotas.length / size)))
  const paginatedQuotas = sortedQuotas.slice((currentPage - 1) * size, currentPage * size)

  React.useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage)
    }
  }, [currentPage, page])

  const handleChangeRowsPerPage = (newSize: number) => {
    setPage(1)
    setSize(newSize)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell width="180px">Progress</TableHeadCell>
              <TableHeadCell width="150px">Status</TableHeadCell>
              <TableHeadCell width="150px">Live Campaigns</TableHeadCell>
              <TableHeadCell width="140px">Actions</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedQuotas.map((quota) => {
              const isDeleteDisabled = quota.campaigns > 0
              const status = getStatus(quota)

              return (
                <TableRow hover key={quota.id}>
                  <TableCell>{quota.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {quota.used} / {quota.goal}
                    </Typography>
                    <LinearProgress
                      aria-label={`${quota.name} progress`}
                      aria-valuetext={`${quota.used} of ${quota.goal}`}
                      color={status.color === 'disabled' ? 'inherit' : status.color}
                      sx={{
                        borderRadius: 1,
                        color: status.color === 'disabled' ? 'action.disabled' : undefined,
                        height: 5,
                        mt: 0.5,
                        width: 96,
                      }}
                      value={getProgress(quota)}
                      variant="determinate"
                    />
                  </TableCell>
                  <TableCell>
                    <IconText>
                      <Icon color={status.color}>{status.icon}</Icon>
                      <Typography>{status.label}</Typography>
                    </IconText>
                  </TableCell>
                  <TableCell>{quota.campaigns}</TableCell>
                  <TableCell>
                    <TableRowActions
                      config={[
                        {
                          disabled: isDeleting || isDeleteDisabled,
                          icon: 'delete',
                          label: 'Delete',
                          tooltip: isDeleteDisabled
                            ? 'This quota is linked to a live campaign and cannot be deleted.'
                            : 'Permanently delete quota',
                          onClick: () => onDeleteQuota(quota),
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
            {!quotas.length && (
              <TableRow>
                <TableCell align="center" colSpan={5}>
                  <Typography color="text.secondary" variant="body2">
                    Create your first quota in the Quotas tab of a survey campaign.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!!quotas.length && (
        <TablePagination
          namespace="quotas"
          page={currentPage}
          pageSizeValues={pageSizeValues}
          size={size}
          total={quotas.length}
          onPageChange={setPage}
          onPageSizeChange={handleChangeRowsPerPage}
        />
      )}
    </>
  )
}

const TableContainer = styled('div')({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflow: 'auto',
  width: '100%',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}))

export default QuotasTable
